/**
 * Builds a `Record<K, V>` from a list of items, one entry per item.
 *
 * TypeScript can't prove an object built up imperatively (starting from `{}`)
 * satisfies a `Record<K, V>` that requires specific keys to exist, so the
 * usual pattern - `array.reduce((acc: Record<K, V>, item) => {...}, {} as Record<K, V>)` -
 * needs an `as` cast at every call site even though the callback itself is
 * fully typed. This contains that one unavoidable cast in a single, tested
 * helper instead of repeating it wherever a Record needs to be built from a
 * list.
 */
export function toRecord<T, K extends PropertyKey, V>(items: readonly T[], getEntry: (item: T) => [K, V]): Record<K, V> {
  const result = {} as Record<K, V>;

  items.forEach((item) => {
    const [key, value] = getEntry(item);
    result[key] = value;
  });

  return result;
}
